import { useContext, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { type FieldValues, type Path, type Control } from 'react-hook-form';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// eslint-disable-next-line import/no-cycle
import { ReadOnlyField } from '@/lib/pages/forms/components';

import { PermissionsContext } from '@/lib/layout/PermissionContext';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Form,
} from '@/components/ui/form';

import { Combobox, type ComboboxOption } from '@/components/ui/combobox';

import { cn } from '@/lib/utils';

export type SelectableLocation<T extends string> = {
  value: T;
  label: string;
  primary?: boolean | undefined;
};

// New interface for the searchable location select
export type LocationOption = ComboboxOption;

export interface SearchableLocationSelectProps<T extends FieldValues> {
  control: Control<T>;
  options: LocationOption[];
  name: Path<T>;
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  orientation?: 'horizontal' | 'vertical';
  required?: boolean;
  stretch?: boolean;
  wideLabel?: boolean;
  readOnly?: boolean;
  overwriteLabelWidth?: string;
  overwriteInputWidth?: string;
  labelClassName?: string;
  nativeRequired?: boolean;
}

export const SearchableLocationSelect = <T extends FieldValues>({
  control,
  options,
  name,
  label,
  placeholder = 'Select location...',
  searchPlaceholder = 'Search locations...',
  disabled,
  orientation = 'vertical',
  required,
  stretch = false,
  wideLabel,
  readOnly,
  overwriteLabelWidth,
  overwriteInputWidth,
  labelClassName,
  nativeRequired = false,
}: SearchableLocationSelectProps<T>) => {
  const { editMode, responsive } = useContext(PermissionsContext);

  // Helper function to calculate input container classes
  const getInputContainerClasses = () => {
    if (orientation === 'vertical') return '';

    let baseWidth = '';
    if (overwriteInputWidth) {
      baseWidth = overwriteInputWidth;
    } else if (stretch) {
      baseWidth = wideLabel ? 'w-1/3' : 'w-2/3';
    } else {
      baseWidth = wideLabel ? 'w-1/5' : 'w-2/5';
    }

    return cn(baseWidth, responsive && 'max-md:w-full', '!mt-0');
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        if (!editMode || readOnly) {
          const optionLabel =
            (options || []).find(
              (option) => String(option.value) === String(field.value)
            )?.label || '';
          return (
            <ReadOnlyField
              label={label}
              value={optionLabel}
              orientation={orientation}
              stretch={stretch}
              wideLabel={wideLabel}
              overwriteInputWidth={overwriteInputWidth}
              overwriteLabelWidth={overwriteLabelWidth}
            />
          );
        }
        return (
          <FormItem
            className={cn(
              'flex',
              orientation === 'horizontal'
                ? 'items-center gap-4'
                : 'w-full flex-col'
            )}
          >
            {label && (
              <FormLabel
                className={cn(
                  (() => {
                    if (orientation !== 'horizontal') return 'mb-2';
                    if (overwriteLabelWidth) return overwriteLabelWidth;
                    if (stretch) return wideLabel ? 'w-2/3' : 'w-1/3';
                    return wideLabel ? 'w-2/5' : 'w-1/5';
                  })(),
                  'font-semibold leading-[1.5rem]',
                  labelClassName
                )}
              >
                {label}
                {(required || nativeRequired) && !disabled && (
                  <span className="text-red-400">*</span>
                )}
              </FormLabel>
            )}
            <div className={getInputContainerClasses()}>
              <FormControl>
                <Combobox
                  options={options}
                  value={field.value ?? ''}
                  onValueChange={field.onChange}
                  placeholder={placeholder}
                  searchPlaceholder={searchPlaceholder}
                  disabled={disabled}
                  emptyMessage="No locations found."
                />
              </FormControl>
              <FormMessage />
            </div>
          </FormItem>
        );
      }}
    />
  );
};

// Legacy component for backward compatibility
export const AlternativeSelect = <T extends FieldValues, U extends string>({
  onChange,
  // Not the best solution, but component was meant to be used specifically for primary location select and has specific logic which we now need to skip to use in different context
  onChangeWithoutDefaultValue,
  options,
  name,
  label,
  placeholder,
  disabled,
  orientation = 'vertical',
  required,
  ignoreReadMode,
  stretch,
  value,
}: {
  onChange: (value: string) => void;
  onChangeWithoutDefaultValue?: (value: string) => void;
  options: Array<SelectableLocation<U>>;
  name: Path<T>;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  orientation?: 'horizontal' | 'vertical';
  required?: boolean;
  ignoreReadMode?: boolean;
  stretch?: boolean;
  value?: string;
}) => {
  const primaryLocation = options.find((option) => option.primary);
  const { editMode } = useContext(PermissionsContext);
  const form = useForm({});

  useEffect(() => {
    if (!primaryLocation?.value && !value && options.length) {
      onChange(options[0].value);
    }
    if (primaryLocation?.value && !value && options.length) {
      onChange(primaryLocation.value);
    }
  }, [onChange, options, primaryLocation, value]);

  const defaultLocation = useMemo(() => {
    if (!primaryLocation?.value && !value && options.length === 1) {
      return options[0].value;
    }
    return '';
  }, []);

  if (!editMode && !ignoreReadMode) {
    return (
      <Form {...form}>
        <ReadOnlyField label={label} value={primaryLocation?.label || ''} />
      </Form>
    );
  }

  return (
    <div
      className={`flex ${orientation === 'horizontal' ? 'items-center gap-4' : 'w-full flex-col'}`}
    >
      {label && (
        <label
          className={`${orientation === 'horizontal' ? `${stretch ? 'w-1/3' : 'w-1/5'}` : 'mb-2'} font-semibold leading-[1.5rem]`}
        >
          {label}
          {required && !disabled && <span className="text-red-400">*</span>}
        </label>
      )}
      <div
        className={`${orientation === 'horizontal' ? `${stretch ? 'w-2/3' : 'w-2/5'}` : ''} !mt-0`}
      >
        <Select
          value={value}
          onValueChange={onChangeWithoutDefaultValue || onChange}
          defaultValue={
            primaryLocation?.value || value || defaultLocation || ''
          }
          disabled={disabled}
        >
          <SelectTrigger tabIndex={0}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map(({ value, label }, idx) => (
              <SelectItem
                key={`${name}_${value}_${idx}`}
                value={value}
                className="min-h-6"
              >
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export { AlternativeSelect as LocationSelect };
